import type { PlacementPosition, Surface, SurfacePoint } from '@repo/api-contracts';
import {
  apply,
  nodeMatrix,
  type PhotoDimensions,
  type PhotoRect,
  type PointMm,
  surfaceMmToPhotoPoint,
  surfacePhotoToMm,
  surfaceScale,
  wrappedPlacementSpans,
} from '@repo/design-renderer';
import type { SurfaceSlot } from './face-surfaces';
import { type BoxNode, type HandleKind, handlePoints } from './handles';

/**
 * 휜(cylinder·mesh) 표면 위 인쇄 영역 하나 — 고객이 휜 모습을 보며 편집할 때 좌표를 옮기는 단위.
 * 조작은 펼친 영역 mm 에서 계산하고, 사진 px 와는 표면의 정·역변환으로 오간다.
 */
export interface WarpedSlot {
  readonly component: SurfaceSlot['component'];
  readonly surface: Surface;
  readonly position: PlacementPosition;
}

export interface WarpedHandle {
  readonly kind: HandleKind;
  readonly x: number;
  readonly y: number;
}

/** 국소 축척을 재는 유한차분 간격(mm). */
const FINITE_DIFFERENCE_MM = 0.5;
/** 핸들이 어느 조각에 속하는지 가르려고 노드 테두리를 나누는 간격(mm). */
const HANDLE_RING_STEP_MM = 1;

/** 원통 둘레(2W) 위 펼친 표면 x 가 몇 번째 바퀴의 앞면 창에 있는지와 창 안 x. 뒷면이면 null. */
const frontWindowOf = (
  surfaceXMm: number,
  frameWidthMm: number,
): { readonly window: number; readonly xMm: number } | null => {
  const window = Math.floor(surfaceXMm / (2 * frameWidthMm));
  const xMm = surfaceXMm - window * 2 * frameWidthMm;
  return xMm <= frameWidthMm ? { window, xMm } : null;
};

/**
 * 사진 px → 영역 로컬 mm. 원통은 감김 조각 중 그 x 를 담는 조각의 원점으로 되돌린다(hit-test-warped.ts 와 같은 규칙).
 * 격자·실루엣 밖이거나 어느 조각에도 없으면 null.
 */
export const photoToComponentMm = (
  slot: WarpedSlot,
  photo: PhotoDimensions,
  point: SurfacePoint,
): PointMm | null => {
  const surfaceMm = surfacePhotoToMm(slot.surface, photo, point);
  if (surfaceMm === null) return null;
  const yMm = surfaceMm.yMm - slot.position.yMm;
  if (slot.surface.type !== 'cylinder') return { xMm: surfaceMm.xMm - slot.position.xMm, yMm };
  const span = wrappedPlacementSpans(
    slot.position.xMm,
    slot.component.widthMm,
    slot.surface.realSize.widthMm,
  ).find((candidate) => surfaceMm.xMm >= candidate.fromMm && surfaceMm.xMm <= candidate.toMm);
  return span === undefined ? null : { xMm: surfaceMm.xMm - span.originMm, yMm };
};

/** 영역 로컬 mm → 사진 px. 원통은 x 를 둘레 2W 로 감아 앞면 [0, W] 만 보이고 뒷면이면 null. */
export const componentMmToPhoto = (
  slot: WarpedSlot,
  photo: PhotoDimensions,
  point: PointMm,
): SurfacePoint | null => {
  const xMm = slot.position.xMm + point.xMm;
  const yMm = slot.position.yMm + point.yMm;
  if (slot.surface.type !== 'cylinder')
    return surfaceMmToPhotoPoint(slot.surface, photo, { xMm, yMm });
  const front = frontWindowOf(xMm, slot.surface.realSize.widthMm);
  return front === null
    ? null
    : surfaceMmToPhotoPoint(slot.surface, photo, { xMm: front.xMm, yMm });
};

/** 휜 테두리 한 조각. closed 면 끊긴 곳 없는 한 바퀴라 마지막 점에서 첫 점으로 이어 그린다(닫는 점은 다시 넣지 않는다). */
export interface WarpedPiece {
  readonly points: readonly SurfacePoint[];
  readonly closed: boolean;
}

interface RingPiece extends WarpedPiece {
  /** 원통 앞면 창 번호(몇 바퀴째 앞면인지). 원통이 아니면 0. */
  readonly window: number;
  /** 조각의 영역 mm 길이. */
  readonly lengthMm: number;
}

/** 변 위 매개변수 — 균등 분할점과, 원통이면 앞·뒷면 경계(펼친 x 가 W 의 배수)를 지나는 점. */
const edgeParams = (slot: WarpedSlot, from: PointMm, to: PointMm, maxStepMm: number): number[] => {
  const length = Math.hypot(to.xMm - from.xMm, to.yMm - from.yMm);
  const count = Math.max(1, Math.ceil(length / maxStepMm));
  const params = Array.from({ length: count }, (_, index) => index / count);
  if (slot.surface.type === 'cylinder' && to.xMm !== from.xMm) {
    const frame = slot.surface.realSize.widthMm;
    const startX = slot.position.xMm + from.xMm;
    const endX = slot.position.xMm + to.xMm;
    const low = Math.ceil(Math.min(startX, endX) / frame);
    const high = Math.floor(Math.max(startX, endX) / frame);
    for (let multiple = low; multiple <= high; multiple += 1) {
      const t = (multiple * frame - startX) / (endX - startX);
      if (t > 0 && t < 1) params.push(t);
    }
  }
  return params.sort((a, b) => a - b);
};

/**
 * 닫힌 테두리(영역 mm)를 잘게 나눠 사진 px 로 옮기고, 보이지 않는 구간·이음매에서 끊은 조각들.
 * 구간은 가운데 점으로 원통 앞면 창을 정하고, 양 끝을 그 창 안 x 로 옮긴다 — 조각 끝이 앞면 가장자리에 닿는다.
 */
const ringPieces = (
  slot: WarpedSlot,
  photo: PhotoDimensions,
  ring: readonly PointMm[],
  maxStepMm: number,
): readonly RingPiece[] => {
  const step = maxStepMm > 0 ? maxStepMm : Number.POSITIVE_INFINITY;
  const samples = ring.flatMap((from, index) => {
    const to = ring[(index + 1) % ring.length] ?? from;
    return edgeParams(slot, from, to, step).map((t) => ({
      xMm: from.xMm + (to.xMm - from.xMm) * t,
      yMm: from.yMm + (to.yMm - from.yMm) * t,
    }));
  });
  if (samples.length < 2) return [];

  const surface = slot.surface;
  const toPhotoIn = (point: PointMm, window: number): SurfacePoint | null => {
    const yMm = slot.position.yMm + point.yMm;
    const xMm = slot.position.xMm + point.xMm;
    if (surface.type !== 'cylinder') return surfaceMmToPhotoPoint(surface, photo, { xMm, yMm });
    const frame = surface.realSize.widthMm;
    const inWindow = Math.min(frame, Math.max(0, xMm - window * 2 * frame));
    return surfaceMmToPhotoPoint(surface, photo, { xMm: inWindow, yMm });
  };
  const intervals = samples.map((from, index) => {
    const to = samples[(index + 1) % samples.length] ?? from;
    const lengthMm = Math.hypot(to.xMm - from.xMm, to.yMm - from.yMm);
    const front =
      surface.type === 'cylinder'
        ? frontWindowOf(slot.position.xMm + (from.xMm + to.xMm) / 2, surface.realSize.widthMm)
        : { window: 0 };
    if (front === null) return null;
    const start = toPhotoIn(from, front.window);
    const end = toPhotoIn(to, front.window);
    return start === null || end === null ? null : { start, end, window: front.window, lengthMm };
  });

  const joins = (previous: (typeof intervals)[number], next: (typeof intervals)[number]): boolean =>
    previous !== null && next !== null && previous.window === next.window;
  const breakAt = intervals.findIndex(
    (interval, index) =>
      !joins(intervals[(index - 1 + intervals.length) % intervals.length] ?? null, interval),
  );
  if (breakAt === -1) {
    // 끊긴 곳 없이 한 바퀴 — 샘플 점 그대로(닫는 점은 첫 점과 같아 다시 넣지 않는다).
    const first = intervals[0];
    return [
      {
        points: intervals.flatMap((interval) => (interval === null ? [] : [interval.start])),
        closed: true,
        window: first?.window ?? 0,
        lengthMm: intervals.reduce((sum, interval) => sum + (interval?.lengthMm ?? 0), 0),
      },
    ];
  }

  const pieces: RingPiece[] = [];
  let current: { points: SurfacePoint[]; closed: false; window: number; lengthMm: number } | null =
    null;
  for (let offset = 0; offset < intervals.length; offset += 1) {
    const interval = intervals[(breakAt + offset) % intervals.length] ?? null;
    if (interval === null) {
      if (current !== null) pieces.push(current);
      current = null;
      continue;
    }
    if (current === null || current.window !== interval.window) {
      if (current !== null) pieces.push(current);
      current = { points: [interval.start], closed: false, window: interval.window, lengthMm: 0 };
    }
    current.points.push(interval.end);
    current.lengthMm += interval.lengthMm;
  }
  if (current !== null) pieces.push(current);
  return pieces.filter((piece) => piece.points.length >= 2);
};

/**
 * 닫힌 테두리(영역 mm)를 휜 사진 위 선 조각들로 — 변을 maxStepMm 이하로 나눠 옮기고, 보이지 않는 구간·원통 이음매에서 끊는다.
 * 끊긴 조각은 열린 선(closed false)이고 점이 2개 미만인 조각은 버린다. 끊긴 곳이 없으면 closed 한 조각이고
 * 닫는 점(첫 점)은 다시 넣지 않는다.
 */
export const warpedRingPieces = (
  slot: WarpedSlot,
  photo: PhotoDimensions,
  ring: readonly PointMm[],
  maxStepMm: number,
): readonly WarpedPiece[] =>
  ringPieces(slot, photo, ring, maxStepMm).map(({ points, closed }) => ({ points, closed }));

/** 한 점에서 영역 mm 1 당 사진 px(축마다) — ±0.5mm 유한차분. 한쪽이 안 보이면 다른 쪽만, 둘 다 없으면 표면 대표 축척. */
export const localPxPerMm = (
  slot: WarpedSlot,
  photo: PhotoDimensions,
  at: PointMm,
): { x: number; y: number } => {
  const fallback = surfaceScale(slot.surface, photo) ?? { x: 1, y: 1 };
  const center = componentMmToPhoto(slot, photo, at);
  const along = (dx: number, dy: number, fallbackValue: number): number => {
    const h = FINITE_DIFFERENCE_MM;
    const ahead = componentMmToPhoto(slot, photo, { xMm: at.xMm + dx * h, yMm: at.yMm + dy * h });
    const behind = componentMmToPhoto(slot, photo, {
      xMm: at.xMm - dx * h,
      yMm: at.yMm - dy * h,
    });
    const [a, b, span] =
      ahead !== null && behind !== null
        ? [ahead, behind, 2 * h]
        : ahead !== null && center !== null
          ? [ahead, center, h]
          : behind !== null && center !== null
            ? [center, behind, h]
            : [null, null, h];
    return a === null || b === null ? fallbackValue : Math.hypot(a.x - b.x, a.y - b.y) / span;
  };
  return { x: along(1, 0, fallback.x), y: along(0, 1, fallback.y) };
};

/** 노드 상자의 네 모서리(영역 mm, 회전 반영). */
const nodeCorners = (node: BoxNode): readonly PointMm[] => {
  const matrix = nodeMatrix(node);
  return [
    [0, 0],
    [node.width, 0],
    [node.width, node.height],
    [0, node.height],
  ].map(([x = 0, y = 0]) => {
    const p = apply(matrix, { x, y });
    return { xMm: p.x, yMm: p.y };
  });
};

/**
 * 선택 노드의 핸들을 휜 사진 px 자리로. 핸들 간격(회전 핸들 24px)은 노드 중심의 국소 축척 × 화면/사진 배율로 잰다.
 * 원통에서 노드 테두리가 이음매로 여러 조각이 되면 테두리 mm 길이가 가장 긴 조각(앞면 창)의 핸들만 남긴다.
 */
export const warpedHandles = (
  slot: WarpedSlot,
  photo: PhotoDimensions,
  node: BoxNode,
  screenPerPhotoPx: number,
): readonly WarpedHandle[] => {
  const center = { xMm: node.x + node.width / 2, yMm: node.y + node.height / 2 };
  const local = localPxPerMm(slot, photo, center);
  const points = handlePoints(node, {
    x: local.x * screenPerPhotoPx,
    y: local.y * screenPerPhotoPx,
  });
  const visible = points.flatMap((handle) => {
    const at = componentMmToPhoto(slot, photo, handle);
    return at === null ? [] : [{ kind: handle.kind, x: at.x, y: at.y, mm: handle }];
  });
  const surface = slot.surface;
  if (surface.type !== 'cylinder') return visible.map(({ kind, x, y }) => ({ kind, x, y }));

  const lengthByWindow = new Map<number, number>();
  for (const piece of ringPieces(slot, photo, nodeCorners(node), HANDLE_RING_STEP_MM))
    lengthByWindow.set(piece.window, (lengthByWindow.get(piece.window) ?? 0) + piece.lengthMm);
  let best: number | null = null;
  for (const [window, length] of lengthByWindow)
    if (best === null || length > (lengthByWindow.get(best) ?? 0)) best = window;
  if (best === null) return [];
  return visible
    .filter(
      ({ mm }) =>
        frontWindowOf(slot.position.xMm + mm.xMm, surface.realSize.widthMm)?.window === best,
    )
    .map(({ kind, x, y }) => ({ kind, x, y }));
};

/** 사진 px 점에서 허용 거리(사진 px) 안의 가장 가까운 핸들. 없으면 null. */
export const hitWarpedHandle = (
  handles: readonly WarpedHandle[],
  point: SurfacePoint,
  tolerancePhotoPx: number,
): HandleKind | null => {
  let nearest: HandleKind | null = null;
  let nearestDistance = tolerancePhotoPx;
  for (const handle of handles) {
    const distance = Math.hypot(handle.x - point.x, handle.y - point.y);
    if (distance <= nearestDistance) {
      nearest = handle.kind;
      nearestDistance = distance;
    }
  }
  return nearest;
};

/**
 * 영역 외곽을 휜 사진 위에 옮긴 외접 상자(사진 px). 원통 뒷면에 다 숨어 보이는 점이 없으면 표면 제어점의 외접 사각형이다.
 */
export const warpedRectOnPhoto = (slot: WarpedSlot, photo: PhotoDimensions): PhotoRect => {
  const { widthMm, heightMm } = slot.component;
  const ring = [
    { xMm: 0, yMm: 0 },
    { xMm: widthMm, yMm: 0 },
    { xMm: widthMm, yMm: heightMm },
    { xMm: 0, yMm: heightMm },
  ];
  const traced = warpedRingPieces(slot, photo, ring, Math.max(widthMm, heightMm) / 64).flatMap(
    (piece) => piece.points,
  );
  const points =
    traced.length > 0
      ? traced
      : slot.surface.points.map((p) => ({
          x: (p.x / 100) * photo.widthPx,
          y: (p.y / 100) * photo.heightPx,
        }));
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);
  return { x: left, y: top, width: Math.max(...xs) - left, height: Math.max(...ys) - top };
};
